import { useCallback, useEffect, useMemo, useState } from 'react';
import { APP_UI_DEFAULTS } from '../../../config/appUiDefaults';

export interface DashboardGroup {
  id: string;
  title: string;
}

export interface WidgetConfig {
  id: string;
  widgetType: string;
  position: { x: number; y: number };
  size: { w: number; h: number };
  settings?: Record<string, unknown>;
  order: number;
  groupId?: string;
}

export interface DashboardLayoutResponse {
  id: number;
  userId: number;
  name: string;
  isDefault: boolean;
  widgets: WidgetConfig[];
  groups?: DashboardGroup[] | null;
  createdAt: string;
  updatedAt: string;
}

const STORAGE_KEY = 'dashboard_layouts_v1';
const STORAGE_MIGRATION_PIPELINE_H2_KEY = 'dashboard_layouts_migrated_pipeline_h2_v1';
/** Layouts authored for the old 6-col seed must be scaled to the live grid. */
const STORAGE_MIGRATION_COLS12_KEY = 'dashboard_layouts_migrated_cols12_v1';
/** One-time seed of curated preset dashboards (Operations, Crew, Purchasing, Finance). */
const STORAGE_PRESETS_KEY = 'dashboard_layouts_presets_v1';
const CANONICAL_GRID_COLS = APP_UI_DEFAULTS.dashboard.gridColumns;
const LEGACY_GRID_COLS = 6;
const DEFAULT_NAME = 'Supplies Dashboard';
let listeners = new Set<() => void>();

const nowIso = () => new Date().toISOString();

function w(
  id: string,
  widgetType: string,
  x: number,
  y: number,
  width: number,
  height: number,
  order: number,
  settings: Record<string, unknown> = {},
): WidgetConfig {
  return {
    id,
    widgetType,
    position: { x, y },
    size: { w: width, h: height },
    settings,
    order,
  };
}

export type DashboardPresetKey = 'supplies' | 'operations' | 'crew' | 'purchasing' | 'finance';

export const DASHBOARD_PRESET_META: Record<
  DashboardPresetKey,
  { name: string; description: string }
> = {
  supplies: { name: DEFAULT_NAME, description: 'Supply purchasing pipeline' },
  operations: { name: 'Operations', description: 'Approvals and PMS' },
  crew: { name: 'Crew', description: 'Crew pool and compliance' },
  purchasing: { name: 'Purchasing', description: 'Supplies and spares pipelines' },
  finance: { name: 'Finance', description: 'Journals, MGA, and rest hours' },
};

/** Canonical Supplies dashboard widgets sized for the live grid (see shared/app-ui-defaults.json). */
export function createDefaultDashboardContent(): {
  widgets: WidgetConfig[];
  groups: DashboardGroup[];
} {
  return createPresetDashboardContent('supplies');
}

/** Curated preset layouts (subset of the widget catalog — not every widget). */
export function createPresetDashboardContent(preset: DashboardPresetKey): {
  widgets: WidgetConfig[];
  groups: DashboardGroup[];
} {
  const full = CANONICAL_GRID_COLS;
  const half = Math.max(1, Math.floor(full / 2));

  switch (preset) {
    case 'operations':
      return {
        groups: [],
        widgets: [
          w('w-approvals-inbox-counters', 'approvals-inbox-counters', 0, 0, full, 2, 0),
          w('w-approvals-recent-pending', 'approvals-recent-pending', 0, 2, half, 2, 1, { limit: 6 }),
          w('w-pms-due-counters', 'pms-due-counters', half, 2, half, 2, 2),
          w('w-pms-recent-work-orders', 'pms-recent-work-orders', 0, 4, half, 2, 3, { limit: 6 }),
        ],
      };
    case 'crew':
      return {
        groups: [],
        widgets: [
          w('w-crew-pool-counters', 'crew-pool-counters', 0, 0, full, 2, 0),
          w('w-crew-recent', 'crew-recent', 0, 2, half, 2, 1, { limit: 6 }),
          w('w-crew-cert-gaps', 'crew-cert-gaps', half, 2, half, 2, 2),
        ],
      };
    case 'purchasing':
      return {
        groups: [],
        widgets: [
          w('w-scms-pipeline-counters', 'scms-pipeline-counters', 0, 0, full, 2, 0),
          w('w-spares-pipeline-counters', 'spares-pipeline-counters', 0, 2, full, 2, 1),
          w('w-scms-recent-deliveries', 'scms-recent-deliveries', 0, 4, half, 2, 2, { limit: 6 }),
          w('w-spares-recent-deliveries', 'spares-recent-deliveries', half, 4, half, 2, 3, { limit: 6 }),
          w('w-scms-top-suppliers-spend', 'scms-top-suppliers-spend', 0, 6, half, 2, 4, { limit: 5 }),
          w('w-spares-top-suppliers-spend', 'spares-top-suppliers-spend', half, 6, half, 2, 5, { limit: 5 }),
        ],
      };
    case 'finance':
      return {
        groups: [],
        widgets: [
          w('w-finance-open-items-counters', 'finance-open-items-counters', 0, 0, full, 2, 0),
          w('w-finance-recent-journals', 'finance-recent-journals', 0, 2, half, 2, 1, { limit: 6 }),
          w('w-finance-period-activity', 'finance-period-activity', half, 2, half, 2, 2),
          w('w-mga-period-counters', 'mga-period-counters', 0, 4, half, 2, 3),
          w('w-mga-recent-periods', 'mga-recent-periods', half, 4, half, 2, 4, { limit: 6 }),
          w('w-wrh-compliance-counters', 'wrh-compliance-counters', 0, 6, half, 2, 5),
          w('w-wrh-recent-sheets', 'wrh-recent-sheets', half, 6, half, 2, 6, { limit: 6 }),
        ],
      };
    case 'supplies':
    default:
      return {
        groups: [],
        widgets: [
          w('w-scms-pipeline-counters', 'scms-pipeline-counters', 0, 0, full, 2, 0),
          w('w-scms-recent-deliveries', 'scms-recent-deliveries', 0, 2, half, 2, 1, { limit: 6 }),
          w('w-scms-recent-invoices', 'scms-recent-invoices', half, 2, half, 2, 2, { limit: 6 }),
          w('w-scms-delivery-line-status', 'scms-delivery-line-status', 0, 4, half, 2, 3),
          w('w-scms-top-suppliers-spend', 'scms-top-suppliers-spend', half, 4, half, 2, 4, { limit: 5 }),
        ],
      };
  }
}

function seedDefaultLayout(): DashboardLayoutResponse {
  const ts = nowIso();
  const { widgets, groups } = createDefaultDashboardContent();
  return {
    id: 1,
    userId: 1,
    name: DEFAULT_NAME,
    isDefault: true,
    widgets,
    groups,
    createdAt: ts,
    updatedAt: ts,
  };
}

function seedPresetLayouts(startId: number): DashboardLayoutResponse[] {
  const ts = nowIso();
  const keys: DashboardPresetKey[] = ['operations', 'crew', 'purchasing', 'finance'];
  return keys.map((key, index) => {
    const { widgets, groups } = createPresetDashboardContent(key);
    return {
      id: startId + index,
      userId: 1,
      name: DASHBOARD_PRESET_META[key].name,
      isDefault: false,
      widgets,
      groups,
      createdAt: ts,
      updatedAt: ts,
    };
  });
}

/** Ensure curated presets exist (by name) without overwriting user-edited layouts of the same name. */
function ensurePresetLayouts(layouts: DashboardLayoutResponse[]): DashboardLayoutResponse[] {
  if (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_PRESETS_KEY) === '1') {
    return layouts;
  }
  const names = new Set(layouts.map((l) => l.name));
  let nextId = layouts.reduce((m, l) => Math.max(m, l.id), 0) + 1;
  const additions: DashboardLayoutResponse[] = [];
  for (const key of ['operations', 'crew', 'purchasing', 'finance'] as DashboardPresetKey[]) {
    const name = DASHBOARD_PRESET_META[key].name;
    if (names.has(name)) continue;
    const { widgets, groups } = createPresetDashboardContent(key);
    additions.push({
      id: nextId++,
      userId: 1,
      name,
      isDefault: false,
      widgets,
      groups,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    });
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_PRESETS_KEY, '1');
  }
  if (additions.length === 0) return layouts;
  const next = [...layouts, ...additions];
  writeLayouts(next);
  return next;
}

function safeParseLayouts(raw: string | null): DashboardLayoutResponse[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as DashboardLayoutResponse[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function layoutMaxColumnSpan(widgets: WidgetConfig[]): number {
  return widgets.reduce((max, w) => Math.max(max, (w.position?.x ?? 0) + (w.size?.w ?? 0)), 0);
}

/** Scale a layout authored for `fromCols` into `toCols` (e.g. legacy 6-col → 12-col). */
function scaleWidgetsToCols(widgets: WidgetConfig[], fromCols: number, toCols: number): WidgetConfig[] {
  if (fromCols === toCols || fromCols <= 0) return widgets;
  const scale = toCols / fromCols;
  return widgets.map((w) => ({
    ...w,
    position: {
      x: Math.min(Math.round((w.position?.x ?? 0) * scale), toCols - 1),
      y: w.position?.y ?? 0,
    },
    size: {
      w: Math.min(Math.max(1, Math.round((w.size?.w ?? 1) * scale)), toCols),
      h: w.size?.h ?? 2,
    },
  }));
}

function normalizeLayoutsForScmsPipeline(layouts: DashboardLayoutResponse[]): DashboardLayoutResponse[] {
  let changed = false;
  const normalized = layouts.map((layout) => {
    const pipeline = layout.widgets.find((w) => w.widgetType === 'scms-pipeline-counters');
    if (!pipeline || pipeline.size.h !== 1) return layout;

    const delta = 2 - pipeline.size.h;
    changed = true;
    const nextWidgets = layout.widgets.map((w) => {
      if (w.id === pipeline.id) {
        return { ...w, size: { ...w.size, h: 2 } };
      }
      if (w.position.y > pipeline.position.y) {
        return { ...w, position: { ...w.position, y: Math.max(0, w.position.y + delta) } };
      }
      return w;
    });

    return {
      ...layout,
      widgets: nextWidgets,
      updatedAt: nowIso(),
    };
  });

  return changed ? normalized : layouts;
}

/** Widen layouts that still use the old 6-col seed so they fill the 12-col grid. */
function normalizeLayoutsFor12Cols(layouts: DashboardLayoutResponse[]): DashboardLayoutResponse[] {
  let changed = false;
  const normalized = layouts.map((layout) => {
    const span = layoutMaxColumnSpan(layout.widgets);
    if (span <= 0 || span > LEGACY_GRID_COLS) return layout;
    changed = true;
    return {
      ...layout,
      widgets: scaleWidgetsToCols(layout.widgets, LEGACY_GRID_COLS, CANONICAL_GRID_COLS),
      updatedAt: nowIso(),
    };
  });
  return changed ? normalized : layouts;
}

function writeLayouts(layouts: DashboardLayoutResponse[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(layouts));
}

function readLayouts(): DashboardLayoutResponse[] {
  const hasMigratedPipelineH2 = localStorage.getItem(STORAGE_MIGRATION_PIPELINE_H2_KEY) === '1';
  const hasMigratedCols12 = localStorage.getItem(STORAGE_MIGRATION_COLS12_KEY) === '1';
  const parsedRaw = safeParseLayouts(localStorage.getItem(STORAGE_KEY));
  let parsed = hasMigratedPipelineH2 ? parsedRaw : normalizeLayoutsForScmsPipeline(parsedRaw);
  if (!hasMigratedPipelineH2) {
    localStorage.setItem(STORAGE_MIGRATION_PIPELINE_H2_KEY, '1');
    if (parsed.length > 0) {
      writeLayouts(parsed);
    }
  }
  if (!hasMigratedCols12) {
    parsed = normalizeLayoutsFor12Cols(parsed);
    localStorage.setItem(STORAGE_MIGRATION_COLS12_KEY, '1');
    if (parsed.length > 0) {
      writeLayouts(parsed);
    }
  }
  // Rename legacy default label once
  let renamed = false;
  parsed = parsed.map((l) => {
    if (l.name !== 'SCMS Dashboard') return l;
    renamed = true;
    return { ...l, name: DEFAULT_NAME, updatedAt: nowIso() };
  });
  if (renamed && parsed.length > 0) {
    writeLayouts(parsed);
  }
  if (parsed.length > 0) {
    return ensurePresetLayouts(parsed);
  }
  const seeded = [seedDefaultLayout(), ...seedPresetLayouts(2)];
  localStorage.setItem(STORAGE_PRESETS_KEY, '1');
  writeLayouts(seeded);
  return seeded;
}

function notifyLayoutsChanged() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function pickLayout(layouts: DashboardLayoutResponse[], layoutName?: string): DashboardLayoutResponse {
  if (layoutName) {
    const byName = layouts.find((l) => l.name === layoutName);
    if (byName) return byName;
  }
  return layouts.find((l) => l.isDefault) ?? layouts[0] ?? seedDefaultLayout();
}

export function useDashboardLayout(layoutName?: string) {
  const [layouts, setLayouts] = useState<DashboardLayoutResponse[]>(() => readLayouts());
  const layout = useMemo(() => pickLayout(layouts, layoutName), [layouts, layoutName]);

  useEffect(() => {
    const refresh = () => setLayouts(readLayouts());
    const unsub = subscribe(refresh);
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      unsub();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const mutate = useCallback(async () => {
    setLayouts(readLayouts());
    return pickLayout(readLayouts(), layoutName);
  }, [layoutName]);

  return { layout, isLoading: false, isError: null, mutate };
}

export function useDashboardLayouts() {
  const [layouts, setLayouts] = useState<DashboardLayoutResponse[]>(() => readLayouts());

  useEffect(() => {
    const refresh = () => setLayouts(readLayouts());
    const unsub = subscribe(refresh);
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      unsub();
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const mutate = useCallback(async () => {
    const next = readLayouts();
    setLayouts(next);
    return next;
  }, []);

  return { layouts, isLoading: false, isError: null, mutate };
}

export function useSaveDashboardLayout() {
  const mutateLayout = useDashboardLayout().mutate;
  const mutateLayouts = useDashboardLayouts().mutate;
  return async (payload: { name?: string; isDefault?: boolean; widgets: WidgetConfig[]; groups?: DashboardGroup[] }) => {
    const layouts = readLayouts();
    const nextId = layouts.reduce((m, l) => Math.max(m, l.id), 0) + 1;
    const ts = nowIso();
    const name = payload.name?.trim() || `Dashboard ${nextId}`;
    let next = layouts;
    if (payload.isDefault) {
      next = next.map((l) => ({ ...l, isDefault: false }));
    }
    const created: DashboardLayoutResponse = {
      id: nextId,
      userId: 1,
      name,
      isDefault: !!payload.isDefault,
      widgets: payload.widgets ?? [],
      groups: payload.groups ?? [],
      createdAt: ts,
      updatedAt: ts,
    };
    next = [...next, created];
    writeLayouts(next);
    notifyLayoutsChanged();
    await Promise.all([mutateLayout(), mutateLayouts()]);
    return created;
  };
}

export function useUpdateDashboardLayout() {
  const mutateLayout = useDashboardLayout().mutate;
  const mutateLayouts = useDashboardLayouts().mutate;
  return async (layoutId: number, payload: { name?: string; isDefault?: boolean; widgets?: WidgetConfig[]; groups?: DashboardGroup[] }) => {
    let layouts = readLayouts();
    if (payload.isDefault) {
      layouts = layouts.map((l) => ({ ...l, isDefault: false }));
    }
    const updatedAt = nowIso();
    const updated = layouts.map((l) =>
      l.id === layoutId
        ? {
            ...l,
            name: payload.name !== undefined ? payload.name : l.name,
            isDefault: payload.isDefault !== undefined ? payload.isDefault : l.isDefault,
            widgets: payload.widgets !== undefined ? payload.widgets : l.widgets,
            groups: payload.groups !== undefined ? payload.groups : l.groups,
            updatedAt,
          }
        : l,
    );
    writeLayouts(updated);
    notifyLayoutsChanged();
    await Promise.all([mutateLayout(), mutateLayouts()]);
    return updated.find((l) => l.id === layoutId);
  };
}

/** Replace a dashboard's widgets/groups with the canonical full-width SCMS seed (keeps name / id / default flag). */
export function useResetDashboardLayoutToDefault() {
  const updateLayout = useUpdateDashboardLayout();
  return async (layoutId: number) => {
    const { widgets, groups } = createDefaultDashboardContent();
    return updateLayout(layoutId, { widgets, groups });
  };
}

export function useDeleteDashboardLayout() {
  const mutateLayout = useDashboardLayout().mutate;
  const mutateLayouts = useDashboardLayouts().mutate;
  return async (layoutId: number) => {
    let layouts = readLayouts().filter((l) => l.id !== layoutId);
    if (layouts.length === 0) {
      layouts = [seedDefaultLayout()];
    }
    if (!layouts.some((l) => l.isDefault)) {
      layouts[0] = { ...layouts[0], isDefault: true, updatedAt: nowIso() };
    }
    writeLayouts(layouts);
    notifyLayoutsChanged();
    await Promise.all([mutateLayout(), mutateLayouts()]);
  };
}

export function useSetDefaultLayout() {
  const mutateLayout = useDashboardLayout().mutate;
  const mutateLayouts = useDashboardLayouts().mutate;
  return async (layoutId: number) => {
    const updatedAt = nowIso();
    const layouts = readLayouts().map((l) => ({
      ...l,
      isDefault: l.id === layoutId,
      updatedAt,
    }));
    writeLayouts(layouts);
    notifyLayoutsChanged();
    await Promise.all([mutateLayout(), mutateLayouts()]);
  };
}
